import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fetchEWODiscripency } from "../../actions/EWODiscripencyAction";

// ------------ Styles ------------
const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StickyCardsContainer = styled(Box)(() => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "10px",
  paddingBottom: "10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  "& .MuiTable-root": { borderCollapse: "separate" },
}));

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "14px 10px",
  fontSize: "14px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154C79",
    color: "#FFFFFF",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": { backgroundColor: "rgba(243, 246, 250, 0.6)" },
  "&:hover": { backgroundColor: "rgba(14, 60, 102, 0.09)", cursor: "pointer" },
}));

export default function EWODiscripency() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.ewoDiscripency);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatDate = (dateObj) => new Date(dateObj).toISOString().split("T")[0];

  useEffect(() => {
    const formatted = formatDate(selectedDate);
    dispatch(fetchEWODiscripency(formatted));
  }, [dispatch, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
  };

  // ---------------- SORTED DATA ----------------
  // Sort purely by Supervisor Name alphabetically
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) =>
      a.EWODis_supName.localeCompare(b.EWODis_supName)
    );
  }, [data]);

  const totalEmployees = sortedData.length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#0e3c66", textAlign: "center", mb: 2 }}>
          EWO Discrepancy
        </Typography>

        {!loading && (
          <StickyCardsContainer>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  startIcon={<CalendarTodayIcon />}
                  onClick={() => setOpenDatePicker(true)}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#142c54",
                    color: "#fff",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: "16px",
                    "&:hover": { backgroundColor: "#0D47A1" },
                  }}
                >
                  {formatDate(selectedDate)}
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Box
                  sx={{
                    background: "#e8f0fa",
                    border: "1px solid #bbd0ea",
                    borderRadius: "20px",
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "#0E3C66",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                  }}
                >
                  Total : {totalEmployees}
                </Box>
              </Grid>
            </Grid>

            {openDatePicker && (
              <DatePicker
                open={openDatePicker}
                value={selectedDate}
                onChange={handleDateChange}
                onClose={() => setOpenDatePicker(false)}
                renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
              />
            )}
          </StickyCardsContainer>
        )}

        <Box sx={{ mt: 2 }}>
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ textAlign: "center" }}>IWO No</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left" }}>Supervisor</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left" }}>Task</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && sortedData?.length > 0 ? (
                  sortedData.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell sx={{ textAlign: "center" }}>{item.EWODis_iwoNo}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>{item.EWODis_supName}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>{item.EWODis_engSpec}</StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={3}>
                      {loading ? "Loading..." : "No Records Found"}
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Box>
      </PageContainer>
    </LocalizationProvider>
  );
}
